<template>
  <main>
    <v-data-table class="data-table" :class="[widthAuto ? 'min-width-auto' : 'min-width-1000',
    !loading && items.length > 0 ? 'elevation-1' : '']" hide-actions :headers="internalHeaders" :items="filteredItems"
      :total-items="totalItems" :pagination.sync="pagination" :loading="loading">

      <!-- Progress bar -->
      <v-progress-circular slot="progress" color="primary" size="60" width="5" class="mt-2" indeterminate />

      <!-- Headers -->
      <template slot="headers" slot-scope="props">
        <tr class="accent elevation-1 header-tr mb-5">

          <!-- Text -->
          <template v-for="header in props.headers">
            <th v-if="header.value !== 'button'" :key="header.value" class="white--text"
              :style="header.width ? `width: ${header.width}%` : `width: calc((100% - 270px) / ${headers.length})`"
              :class="['column',
                header.right ? 'text-xs-right' : 'text-xs-left',
                header.sortable ? 'sortable' : '',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : '']"
              @click="header.sortable && changeSort(header.value)">

              <v-icon v-if="header.sortable && header.right" small color="white">arrow_upward</v-icon>
              <v-tooltip bottom>
                <span class="mx-1" slot="activator">{{ header.text }}</span>
                <span>{{ header.info || header.text }}</span>
              </v-tooltip>
              <v-icon v-if="header.sortable && !header.right" small color="white">arrow_upward</v-icon>
            </th>

            <!-- Toggle button -->
            <th v-else :key="header.value" class="toggle-container">
              <slot name="headerButton">
                <v-btn v-if="showHeaderButton" @click="internalToggled = !internalToggled" block round dark depressed
                  color="rgba(255,255,255,0.16)" class="ma-0 mr-2 height-36">
                  <v-icon class="mr-2" small>{{ internalToggled ? 'format_list_bulleted' : 'delete_outline' }}</v-icon>
                  <span>{{ internalToggled ? $t('label.attivi') : $t('label.eliminati') }}</span>
                </v-btn>
              </slot>
            </th>
          </template>


        </tr>
      </template>

      <!-- Items -->
      <template slot="items" slot-scope="{ item, index }" v-if="!loading">
        <tr class="elevation-1 items-container" @click="$emit('click:item', item, items.indexOf(item))">
          <slot name="items" :item="item" :index="index" />
        </tr>
      </template>

      <!-- Footer -->
      <template slot="footer">
        <slot name="footer" />
      </template>

      <!-- Empty message -->
      <template slot="no-data">
        <div class="headline text-xs-center">{{ !loading ? noDataText || $t('errori.vuoto') : '' }}</div>
      </template>

      <!-- No result message -->
      <template slot="no-results">
        <div class="headline text-xs-center py-3">
        {{ !loading ? $t('errori.nessun_risultato') : '' }}
        <span v-if="search">"{{ search }}"</span>
      </div>

      </template>

    </v-data-table>

    <!-- Righe aggiuntive per avere i pulsanti in fondo sempre alla stessa altezza -->
    <table v-if="rowsPerPage > 0 && !loading && pages > 1 && pagination.page === pages" class="v-datatable v-table">
      <tr v-for="i in ((pages * rowsPerPage) - pagination.totalItems)" :key="i" class="height-48"></tr>
    </table>

    <div v-if="rowsPerPage > 0 && !loading && items.length > 0 && pages > 1" class="text-xs-center mt-2">
      <v-pagination color="accent" circle v-model="pagination.page" :length="pages" total-visible="10" />
    </div>

  </main>
</template>

<script>

export default {
  name: 'DataTable',
  props: {
    headers: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    initialSort: {
      type: String,
      default: 'id'
    },
    descending: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: undefined
    },
    totalItems: {
      type: Number,
      default: undefined
    },
    rowsPerPage: {
      type: Number,
      default: -1
    },
    noDataText: String,
    widthAuto: Boolean,
    loading: Boolean,
    showHeaderButton: Boolean,
    toggled: Boolean
  },
  data() {
    return {
      lastPage: 1,
      pagination: {
        page: 1,
        search: '',
        sortBy: this.initialSort,
        descending: this.descending,
        rowsPerPage: this.rowsPerPage,
        totalItems: this.items.length
      }
    }
  },
  methods: {
    resetPage() {
      this.pagination.page = 1
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    }
  },
  computed: {
    internalToggled: {
      get() {
        return this.toggled
      },
      set(val) {
        this.$emit('update:toggled', val)
      }
    },
    filteredItems() {
      if (!this.search) return this.items;

      // suddivide la ricerca in parole
      const terms = this.search.toLowerCase().trim().split(/\s+/);

      return this.items.filter(item => {
        const fullText = this.headers
          .map(h => {
            const value = item[h.value];
            return value != null ? String(value).toLowerCase() : '';
          })
          .join(' ');

        return terms.every(term => fullText.includes(term));
      });
    },
    internalHeaders() {
      return this.showHeaderButton ? this.headers.concat([{ value: 'button' }]) : this.headers
    },
    pages() {
      if (this.pagination.rowsPerPage === null || this.pagination.rowsPerPage < 0 || this.pagination.totalItems === null || this.pagination.totalItems <= this.pagination.rowsPerPage) {
        return 0
      }
      return Math.ceil(this.pagination.totalItems / this.pagination.rowsPerPage)
    }
  },
  watch: {
    items() {
      this.$nextTick(() => {
        this.pagination.totalItems = this.totalItems || this.items.length
      })
    },
    pagination: {
      handler(val) {
        if (val.page !== this.lastPage) {
          this.lastPage = val.page
          this.$emit('page', val.page)
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
.data-table {

  &.min-width- {
    &1000 table {
      min-width: 1000px;
    }

    &auto table {
      min-width: auto;
    }
  }

  table {
    table-layout: fixed;
  }

  .header-tr {
    height: 48px !important;
    border-radius: 2px;
  }

  th,
  td {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px !important;
    padding: 0 !important;
  }

  tr td:first-child,
  tr th:first-child {
    padding-left: 24px !important;
  }

  th {
    font-weight: 500 !important;

    &.toggle-container {
      width: 320px;
      padding-left: 24px !important;
      padding-right: 8px !important;
    }
  }

  td {
    font-weight: 700 !important;
  }

  tr.items-container {
    td {
      cursor: pointer;
      position: relative;
    }

    .v-btn {
      float: right;
    }
  }
}
</style>
